import type { EntitiesV2HistoryPostData } from "@/types/ls-api";

/** まだ投稿されていない投稿かどうか */
export function isNotYetPublished(post: EntitiesV2HistoryPostData): boolean {
    return post?.state === "INCOMPLETE" || post?.state === "READY";
}

/**
 * 新規投稿（確定済みの内容が無い投稿）かどうか。
 * TM-9322 U3: サーバーが一覧の行に載せた hasConfirmedContent を使う。子ファイルの状態や数では判定しない
 * （ファイル一括投稿の承認済みでまだどの店舗にも投稿されていない投稿を、新規と取り違えるため）
 */
async function isNewPost(post: EntitiesV2HistoryPostData): Promise<boolean> {
    return !(post?.hasConfirmedContent ?? false);
}

/** 新規投稿の承認要求かどうか */
export async function isNewRequest(post: EntitiesV2HistoryPostData): Promise<boolean> {
    const isNew = await isNewPost(post);
    return isNew && post?.state === "JUDGEMENTREQUEST";
}

/** 新規投稿のactionTypeを判定 */
export function determineNewActionType(
    post: EntitiesV2HistoryPostData,
    isRequestAuthorization: boolean
): string {
    if (isRequestAuthorization) {
        return post?.xlsxMode ? "v2-post-new-file-requested" : "v2-post-new-requested";
    }
    return post?.xlsxMode ? "v2-post-new-file-reserved" : "v2-post-new-reserved";
}

/** 投稿編集のactionTypeを判定 */
export async function determineEditActionType(
    post: EntitiesV2HistoryPostData,
    isRequestAuthorization: boolean
): Promise<string> {
    // 却下後の編集等の場合は新規投稿として扱う
    const isNew = await isNewPost(post);
    if (isNew) {
        return determineNewActionType(post, isRequestAuthorization);
    }
    if (isRequestAuthorization) {
        return post?.xlsxMode ? "v2-post-edit-file-requested" : "v2-post-edit-requested";
    }
    return post?.xlsxMode ? "v2-post-edit-file-reserved" : "v2-post-edit-reserved";
}

/**
 * ゴミ箱で起きること（TM-9322 U3 BR-U3-1）。サーバーがゴミ箱の処理と同じ判定で決め、一覧の行に載せる。
 * 画面は押す人の権限で片方を読むだけで、判断を写し直さない（写し直すと、サーバーの分岐を変えたときに案内が食い違う）
 */
export type TrashAction =
    | "discardNewDraft"
    | "discardDraft"
    | "withdrawRequest"
    | "withdrawNewRequest"
    | "requestDeletion"
    | "delete";

/** ゴミ箱で起きること。申請者（承認申請権限の利用者）か、削除権限のある利用者かで読む値を変える */
export function trashActionOf(
    post: EntitiesV2HistoryPostData,
    isRequestAuthorization: boolean
): TrashAction {
    const action = isRequestAuthorization ? post?.trashActionByRequester : post?.trashActionByDeleter;
    return (action ?? "delete") as TrashAction;
}

/** ゴミ箱の確認ダイアログの文言。題は「{target}を{processName}してもよろしいですか？」 */
export type TrashDialog = {
    target: string;
    processName: string;
    notice: string;
    /** 戻せない結果の注意（赤字で出す） */
    warning: string;
    /** 承認者の選択と理由欄を出すか（削除の申請だけ） */
    isApproveRequest: boolean;
    defaultRemark: string;
    /** 完了のスナックバー。直前の案内と逆のことを言わない */
    doneMessage: string;
};

/** ゴミ箱で起きることごとの案内（TM-9322 U3 BR-U3-2）。投稿が残る操作で「投稿を削除」と案内しない */
export const trashDialogs: Record<TrashAction, TrashDialog> = {
    discardNewDraft: {
        target: "下書きの投稿",
        processName: "削除",
        notice: "一時保存（新規）の投稿を削除します。",
        isApproveRequest: false,
        defaultRemark: "",
        warning: "",
        doneMessage: "削除を開始しました",
    },
    discardDraft: {
        target: "一時保存した内容",
        processName: "破棄",
        notice: "一時保存した内容を破棄し、一時保存する前の内容と予約に戻します。投稿は削除されません。",
        isApproveRequest: false,
        defaultRemark: "",
        warning: "",
        doneMessage: "一時保存した内容を破棄しました",
    },
    withdrawRequest: {
        target: "承認依頼",
        processName: "撤回",
        notice: "承認依頼を撤回し、承認依頼をする前の内容と予約に戻します。投稿は削除されません。",
        isApproveRequest: false,
        defaultRemark: "",
        warning: "",
        doneMessage: "承認依頼を撤回しました",
    },
    withdrawNewRequest: {
        target: "承認依頼",
        processName: "撤回",
        notice: "承認依頼を撤回します。",
        warning: "この投稿はまだ一度も承認されていないため、撤回すると投稿は残りません。",
        isApproveRequest: false,
        defaultRemark: "",
        doneMessage: "承認依頼を撤回しました",
    },
    requestDeletion: {
        target: "投稿",
        processName: "削除依頼",
        notice:
            "投稿の削除を承認者に依頼します。承認されるまで投稿は削除されず、この投稿の予約は止まります。却下・撤回されると、依頼する前の予約に戻ります。",
        isApproveRequest: true,
        defaultRemark: "削除依頼です（システムによる自動入力）",
        warning: "",
        doneMessage: "削除の承認依頼を送信しました",
    },
    delete: {
        target: "投稿",
        processName: "削除",
        notice: "",
        isApproveRequest: false,
        defaultRemark: "",
        warning: "",
        doneMessage: "削除を開始しました",
    },
};

/** 投稿削除のactionTypeを判定（操作記録の種別）。ゴミ箱で起きることはサーバーが行に載せた値を使う（TM-9322 U3 BR-U3-1） */
export async function determineDeleteActionType(
    post: EntitiesV2HistoryPostData,
    isRequestAuthorization: boolean
): Promise<string> {
    const action = trashActionOf(post, isRequestAuthorization);
    switch (action) {
        case "discardDraft":
            return "v2-post-delete-incomplete";
        case "discardNewDraft":
            return "v2-post-delete";
        case "withdrawNewRequest":
            return post?.xlsxMode ? "v2-post-new-file-request-withdrawn" : "v2-post-new-request-withdrawn";
        case "withdrawRequest":
            if (post?.judgementRequestIsDelete) {
                return post?.xlsxMode
                    ? "v2-post-delete-file-request-withdrawn"
                    : "v2-post-delete-request-withdrawn";
            }
            return post?.xlsxMode
                ? "v2-post-edit-file-request-withdrawn"
                : "v2-post-edit-request-withdrawn";
        case "requestDeletion":
            return post?.xlsxMode ? "v2-post-delete-file-requested" : "v2-post-delete-requested";
        case "delete":
            // 下の判定へ進む
            break;
        default: {
            // TrashAction に値が増えたら、ここが型エラーになって更新漏れを知らせる
            const unhandled: never = action;
            return unhandled;
        }
    }
    // 削除。申請者が削除になるのは、却下された新規の投稿（まだどの店舗にも投稿されていない）だけである。
    // まだどの店舗にも投稿されていない投稿（READY 等）は「指示ファイル削除」処理指示として扱う(backendの仕様)。
    // 削除権限のある利用者が未承認の新規の投稿（申請中・却下）を消すときも同じ（TM-9322 U1 BR-U1-12）
    if (
        isRequestAuthorization ||
        !post?.hasConfirmedContent ||
        post?.state == "DELETEFINISHED" ||
        post?.state == "READY" ||
        post?.state == "FAILED"
    ) {
        return "v2-post-delete";
    }
    // その他は一括削除処理指示として扱う(ファイル一括とそうでない投稿と区別をつける)
    return post?.xlsxMode ? "v2-post-delete-file-reserved" : "v2-post-delete-reserved";
}

/** 投稿再実行のactionTypeを判定 */
export function determineRetryActionType(post: EntitiesV2HistoryPostData): string {
    return post?.xlsxMode ? "v2-post-retry-file-reserved" : "v2-post-retry-reserved";
}

/** 投稿承認のactionTypeを判定 */
export async function determineApproveActionType(post: EntitiesV2HistoryPostData): Promise<string> {
    if (post.judgementRequestIsDelete) {
        return post?.xlsxMode ? "v2-post-delete-file-approved" : "v2-post-delete-approved";
    } else if (await isNewPost(post)) {
        return post?.xlsxMode ? "v2-post-new-file-approved" : "v2-post-new-approved";
    } else {
        return post?.xlsxMode ? "v2-post-edit-file-approved" : "v2-post-edit-approved";
    }
}

/** 投稿承認依頼却下のactionTypeを判定 */
export async function determineRejectActionType(post: EntitiesV2HistoryPostData): Promise<string> {
    if (post.judgementRequestIsDelete) {
        return post?.xlsxMode ? "v2-post-delete-file-rejected" : "v2-post-delete-rejected";
    } else if (await isNewPost(post)) {
        return post?.xlsxMode ? "v2-post-new-file-rejected" : "v2-post-new-rejected";
    } else {
        return post?.xlsxMode ? "v2-post-edit-file-rejected" : "v2-post-edit-rejected";
    }
}

/**
 * 入口のダイアログの案内（TM-9322 U3 BR-U3-3〜BR-U3-5）。
 * 予約の案内は、確定済みの内容がある投稿にだけ出す（新規の投稿には止まる予約が無い）。「この投稿の」を必ず入れる
 */
export const entryNotices = {
    /** 一時保存（確定済みの内容がある投稿） */
    draft: "一時保存している間、この投稿の予約（掲載開始・掲載終了）は止まります。一時保存した内容を確定すると（承認依頼の場合は承認されると）その内容で、一時保存を破棄すると一時保存する前の内容で、予約を設定し直します。",
    /** 申請中の投稿の一時保存（TM-9322 U1 BR-U1-11） */
    draftWithdrawsRequest:
        "一時保存すると、出している承認依頼は撤回され、承認待ちから外れます。もう一度「承認依頼」を押すまで、承認されません。",
    /** 編集の申請（確定済みの内容がある投稿） */
    editRequest:
        "承認されるまで、この投稿の予約（掲載開始・掲載終了）は止まります。承認されると承認依頼した内容で、却下・撤回されると承認依頼する前の内容で、予約を設定し直します。",
    /** 予約のある投稿の申請。承認の前に予約が設定されているように案内しない */
    reservedAfterApproval: "承認されると、以下の日時に予約投稿を設定します。",
    /** 予約の無い投稿の申請。承認の前に処理が始まるように案内しない */
    immediateAfterApproval: "承認されると、即時投稿処理を開始します。",
};
